import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../config/api";
import AboutIntroSlider from "./AboutIntroSlider";
import OurCinemasSlider from "./OurCinemasSlider";
import "./AboutUs.css";

// ─────────────────────────────────────────────────────────────────────────────
//  SVG icons (inlined so the page works without any extra asset requests and
//  matches the HTML reference design exactly).
// ─────────────────────────────────────────────────────────────────────────────

// Home icon — reused from the navbar (Header.js) and AdvertiseHero.js
// (same SVG path). Fill is #6E727B so it matches the breadcrumb link
// colour. The clipPath id is renamed so it doesn't collide with the
// navbar's clipPath id (clip0_478_766) — SVG clipPath ids are global
// per document and the last one rendered wins.
const HomeIconSvg = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g clipPath="url(#clip0_breadcrumb_home)">
      <path
        d="M11.0833 13.9997H2.91667C1.30842 13.9997 0 12.6913 0 11.083V5.67203C0 4.70078 0.480667 3.79661 1.28567 3.25411L5.36842 0.498445C6.3595 -0.170055 7.6405 -0.170055 8.63158 0.498445L12.7149 3.25411C13.5193 3.79661 14 4.7002 14 5.67203V11.083C14 12.6913 12.6916 13.9997 11.0833 13.9997ZM7 1.16461C6.65933 1.16461 6.31867 1.26495 6.02117 1.4662L1.93783 4.22128C1.45483 4.54678 1.16667 5.0887 1.16667 5.67145V11.0824C1.16667 12.0473 1.95183 12.8324 2.91667 12.8324H11.0833C12.0482 12.8324 12.8333 12.0473 12.8333 11.0824V5.67203C12.8333 5.08928 12.5452 4.54678 12.0627 4.22186L7.97883 1.4662C7.68133 1.26495 7.34067 1.16461 7 1.16461Z"
        fill="#6E727B"
      />
    </g>
    <defs>
      <clipPath id="clip0_breadcrumb_home">
        <rect width="14" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// Location pin — used inside statistics cards next to location text.
// Matches assets/img/location.svg from the HTML reference design.
const LocationIconSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 0C4.68667 0 2 2.68667 2 6C2 10 8 16 8 16C8 16 14 10 14 6C14 2.68667 11.3133 0 8 0ZM8 8C6.9 8 6 7.1 6 6C6 4.9 6.9 4 8 4C9.1 4 10 4.9 10 6C10 7.1 9.1 8 8 8Z"
      fill="#E5B868"
    />
  </svg>
);

// Globe — used inside statistics cards for international reach.
// Matches assets/img/globe.svg from the HTML reference design.
const GlobeIconSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 0C3.58867 0 0 3.58867 0 8C0 12.4113 3.58867 16 8 16C12.4113 16 16 12.4113 16 8C16 3.58867 12.4113 0 8 0ZM14.6667 7.33333H12.2867C12.192 5.726 11.9027 4.26667 11.4587 3.07867C12.9227 3.92 14.1307 5.49667 14.6667 7.33333ZM8 1.33333C8.59 1.33333 9.72867 2.71667 10.1907 4.93067C10.292 5.35867 10.374 5.81867 10.436 6.302C10.476 6.638 10.508 6.98267 10.5307 7.33333H5.46933C5.49133 6.98267 5.524 6.638 5.564 6.302C5.626 5.81867 5.708 5.35867 5.80933 4.93067C6.27133 2.71667 7.41 1.33333 8 1.33333ZM5.54133 3.07867C5.09733 4.26667 4.808 5.726 4.71333 7.33333H2.33333C2.86933 5.49667 4.07733 3.92 5.54133 3.07867ZM1.33333 8C1.33333 8.224 1.34667 8.44467 1.37133 8.66667H4.66667C4.652 9.01467 4.66667 9.35467 4.66667 9.66667C4.66667 10.0587 4.68267 10.4347 4.70667 10.8C4.74867 11.408 4.81267 11.9867 4.9 12.5347C2.874 11.6747 1.33333 9.98867 1.33333 8ZM8 14.6667C7.404 14.6667 6.26933 13.2727 5.80933 11.0587C5.708 10.6307 5.626 10.1727 5.564 9.692C5.524 9.356 5.49133 9.012 5.46933 8.66667H10.5307C10.508 9.012 10.476 9.356 10.436 9.692C10.374 10.1727 10.292 10.6307 10.1907 11.0587C9.72867 13.2727 8.59 14.6667 8 14.6667ZM11.4587 12.5347C11.9027 11.3467 12.192 9.88667 12.2867 8.27867H14.7147C14.6907 8.50067 14.6667 8.72067 14.6667 8.94467V8C14.6667 8 14.6667 8 14.6667 8C14.6667 8 14.6667 8 14.6667 8V8.66667C14.6667 9.98667 13.1253 11.6727 11.4587 12.5347Z"
      fill="#E5B868"
    />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 12L10 8L6 4"
      stroke="#6E727B"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// "Navigate" / direction arrow used in the HTML reference's level
// tag (<svg width="12" height="12" viewBox="0 0 12 12">). Inlined
// here so we don't need to ship another static asset.
const NavigateIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1.5 5.5L11 1L6.5 10.5L5.5 6.5L1.5 5.5Z"
      stroke="#432DD7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Page-level constants
// ─────────────────────────────────────────────────────────────────────────────

// Breadcrumb is hard-coded to "Home → About Us" (matches the image
// reference). If you later want it dynamic, store it in
// cms_page_configs.data.breadcrumb instead.
const PAGE_KEY = "about-us";
const BREADCRUMB = [{ label: "Home", link: "/" }];

// Renders the icon shown before a breadcrumb label.
const renderBreadcrumbIcon = (label) => {
  if (label === "Home") return <HomeIconSvg />;
  return null;
};

// Picks the right small icon for a stat card based on icon_type
// ("location" → pin, "globe" → globe). Matches assets/img/*.svg
// from the HTML reference design.
const renderStatIcon = (iconType) => {
  if (iconType === "globe") return <GlobeIconSvg />;
  return <LocationIconSvg />;
};

// ─────────────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────────────

const AboutUs = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Our Cinemas — section copy + cinema list (heading, subheading,
  // and the per-cinema images / level / heading / description).
  // Fetched in parallel with the page config; never blocks the
  // rest of the page (the api helper falls back to a safe empty
  // payload if the endpoint is down).
  const [ourCinemasData, setOurCinemasData] = useState({
    heading: "Our Cinemas",
    subheading: "",
    cinemas: [],
  });

  useEffect(() => {
    let isMounted = true;
    const fetchPageData = async () => {
      try {
        const response = await api.getPageConfigByKey(PAGE_KEY);
        if (response && response.id) {
          if (isMounted) setPageData(response);
        } else {
          if (isMounted) setError("No data received from API");
        }
      } catch (err) {
        console.error("Failed to fetch About Us page config:", err);
        if (isMounted) setError(err.message || "Failed to load page data");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPageData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch the public Our Cinemas data in parallel. The api helper
  // resolves to a safe empty payload on failure so this never
  // throws or blocks the rest of the page.
  useEffect(() => {
    let isMounted = true;
    const fetchOurCinemas = async () => {
      try {
        const data = await api.getOurCinemas();
        if (isMounted && data) {
          setOurCinemasData({
            heading: data.heading || "Our Cinemas",
            subheading: data.subheading || "",
            cinemas: Array.isArray(data.cinemas) ? data.cinemas : [],
          });
        }
      } catch (err) {
        console.warn("Failed to fetch Our Cinemas data:", err);
        if (isMounted) {
          setOurCinemasData({
            heading: "Our Cinemas",
            subheading: "",
            cinemas: [],
          });
        }
      }
    };
    fetchOurCinemas();
    return () => {
      isMounted = false;
    };
  }, []);

  const metaTitle = pageData?.meta_title || pageData?.title || "About Us";
  const metaDescription =
    pageData?.meta_description ||
    pageData?.description ||
    "Learn about MY Cinemas.";

  useEffect(() => {
    const previousTitle = document.title;
    document.title = metaTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", metaDescription);
    return () => {
      document.title = previousTitle;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaTitle, metaDescription]);

  if (loading) {
    return (
      <div className="about-us-page d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="about-us-page container py-5">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Unable to load About Us page</h4>
          <p className="mb-0">
            {error ||
              "No data available. Please configure the About Us page in the admin panel."}
          </p>
        </div>
      </div>
    );
  }

  // ---- Transform API data ----
  const allSections = pageData.sections || [];
  const getSectionByType = (type) =>
    allSections.find((s) => s.is_active && s.data?.section_type === type);

  const heroSection = getSectionByType("hero");
  const introSection = getSectionByType("intro");
  const statisticsSection = getSectionByType("statistics");
  const whatWeDoSection = getSectionByType("what_we_do");
  const missionVisionSection = getSectionByType("mission_vision");

  const statsData = statisticsSection?.data || {};
  const statsList = Array.isArray(statsData.stats) ? statsData.stats : [];

  const whatWeDoData = whatWeDoSection?.data || {};
  const servicesList = Array.isArray(whatWeDoData.services)
    ? whatWeDoData.services
    : [];

  const mvData = missionVisionSection?.data || {};
  const visionData = mvData.vision || {};
  const missionData = mvData.mission || {};

  // Admin-uploaded images are full URLs.
  const normalizeImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http") || url.startsWith("/")) return url;
    return `/${url}`;
  };

  return (
    <div className="about-us-page">
      {/* --------------------  Section 1: Hero / Banner  -------------------- */}
      {heroSection && (
        <div className="inner-banner">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <ul className="breadcrumps">
                  {BREADCRUMB.map((crumb, idx) => (
                    <React.Fragment key={idx}>
                      <li>
                        <Link to={crumb.link}>
                          {renderBreadcrumbIcon(crumb.label)}
                          {crumb.label}
                        </Link>
                      </li>
                      <li>
                        <ChevronRight />
                      </li>
                    </React.Fragment>
                  ))}
                  <li className="breadcrumps-current">{pageData.title}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --------------------  Section 2: Intro (AARVJ Group)  -------------------- */}
      {introSection && (
        <div className="about-container">
          <div className="container">
            <div className="row">
              <div className="col-xl-6">
                {introSection.data?.heading && (
                  <h4>{introSection.data.heading}</h4>
                )}
                {introSection.data?.content && (
                  <div
                    className="rich-content"
                    dangerouslySetInnerHTML={{
                      __html: introSection.data.content,
                    }}
                  />
                )}
              </div>
              <div className="col-xl-6">
                {/* Admin can upload a list of slider_images. Falls back to the
                    legacy single image_url field for backward compatibility.
                    If neither is set, the column renders empty (matches the
                    HTML reference behaviour where the right column is optional).
                    The <figure class="m-0"> wrapper matches the HTML reference
                    markup so the existing .about-container figure padding
                    still applies and margins are reset exactly like HTML. */}
                {(Array.isArray(introSection.data?.slider_images) &&
                  introSection.data.slider_images.length > 0) ||
                introSection.data?.image_url ? (
                  <figure className="m-0">
                    <AboutIntroSlider
                      images={
                        Array.isArray(introSection.data?.slider_images) &&
                        introSection.data.slider_images.length > 0
                          ? introSection.data.slider_images
                          : introSection.data?.image_url
                            ? [introSection.data.image_url]
                            : []
                      }
                      // Slider renders each image at its natural aspect
                      // ratio — matches the static about.html reference
                      // (width:100%; height:auto on the <img>).
                    />
                  </figure>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --------------------  Section 3: Statistics / Landmark  -------------------- */}
      {statisticsSection && (
        <div
          className="statistics-section"
          style={{
            backgroundImage: statsData.background_image
              ? `url(${normalizeImageUrl(statsData.background_image)})`
              : "none",
          }}
        >
          <div className="container">
            <div className="row">
              {statsData.left_image_url && (
                <div className="col-xl-6">
                  <figure className="m-0">
                    <img
                      src={normalizeImageUrl(statsData.left_image_url)}
                      alt="Landmark"
                    />
                  </figure>
                </div>
              )}
              <div
                className={statsData.left_image_url ? "col-xl-6" : "col-xl-12"}
              >
                <div className="statistics-col">
                  {statsData.since_text && <h5>{statsData.since_text}</h5>}
                  {statsData.target_text && (
                    <div className="quote">{statsData.target_text}</div>
                  )}
                  {statsList.length > 0 && (
                    <div className="row">
                      {statsList.map((s, i) => (
                        <div
                          key={i}
                          className={i === 0 ? "col-md-12 mb-4" : "col-md-6"}
                        >
                          <div className="stat-col">
                            <h3>
                              {s.number}
                              {s.label1 && <span>{s.label1}</span>}
                              {s.label2 && <span>{s.label2}</span>}
                              {s.label3 && <span>{s.label3}</span>}
                            </h3>
                            {s.text && (
                              <div className="stat-location">
                                {renderStatIcon(s.icon_type)}
                                <span>{s.text}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --------------------  Section 4: Our Cinemas (tab list)  -------------------- */}
      {ourCinemasData.cinemas && ourCinemasData.cinemas.length > 0 && (
        <section className="our_cinemas">
          <div className="container">
            <div className="row">
              <div className="col-md-12 head-area">
                <h2>{ourCinemasData.heading}</h2>
                {ourCinemasData.subheading && (
                  <p>{ourCinemasData.subheading}</p>
                )}
              </div>
            </div>
            <div className="row our_cinemas-row">
              <ul
                className="nav nav-pills flex-column nav-pills"
                id="our-cinemas-pills"
                role="tablist"
              >
                {ourCinemasData.cinemas.map((cinema, idx) => {
                  const tabId = `our-cinemas-tab-${idx}`;
                  const panelId = `our-cinemas-panel-${idx}`;
                  return (
                    <li className="nav-item" role="presentation" key={cinema.id}>
                      <button
                        className={`nav-link${idx === 0 ? " active" : ""}`}
                        id={`${tabId}-tab`}
                        data-bs-toggle="pill"
                        data-bs-target={`#${panelId}`}
                        type="button"
                        role="tab"
                        aria-controls={panelId}
                        aria-selected={idx === 0 ? "true" : "false"}
                      >
                        {cinema.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div
                className="tab-content our-cinemas-area"
                id="our-cinemas-pillsContent"
              >
                {ourCinemasData.cinemas.map((cinema, idx) => {
                  const tabId = `our-cinemas-tab-${idx}`;
                  const panelId = `our-cinemas-panel-${idx}`;
                  // Build the Google Maps link only when both lat/lng
                  // are valid numbers — otherwise we render the level
                  // text as a non-clickable <span> instead of an <a>.
                  const lat = Number(cinema.latitude);
                  const lng = Number(cinema.longitude);
                  const hasLatLng =
                    Number.isFinite(lat) &&
                    Number.isFinite(lng) &&
                    lat !== 0 &&
                    lng !== 0;
                  const mapsUrl = hasLatLng
                    ? `https://www.google.com/maps?q=${lat},${lng}`
                    : null;
                  const hasImages =
                    Array.isArray(cinema.images) && cinema.images.length > 0;
                  const fallbackImage = !hasImages ? cinema.imageUrl : null;
                  return (
                    <div
                      className={`tab-pane fade${idx === 0 ? " show active" : ""}`}
                      id={panelId}
                      role="tabpanel"
                      aria-labelledby={`${tabId}-tab`}
                      key={cinema.id}
                    >
                      <div className="our-cinema-col">
                        {(hasImages || fallbackImage) && (
                          <OurCinemasSlider
                            images={cinema.images}
                            fallbackImage={fallbackImage}
                            alt={cinema.name}
                          />
                        )}
                        <div className="my-cinemas-container">
                          <div className="my-cinemas-col-tab">
                            {cinema.levelText && (
                              hasLatLng ? (
                                <a
                                  className="tags level-tag"
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open in Google Maps"
                                >
                                  <NavigateIcon />
                                  {cinema.levelText}
                                </a>
                              ) : (
                                <span className="tags">
                                  <NavigateIcon />
                                  {cinema.levelText}
                                </span>
                              )
                            )}
                            <div className="right-tags">
                              <div className="tags">
                                {cinema.screens || 0} Screens
                              </div>
                              <div className="tags">
                                {cinema.totalSeats || 0} Seatings
                              </div>
                            </div>
                          </div>
                          <h4>{cinema.heading}</h4>
                          <p>{cinema.description}</p>
                          <Link to={cinema.detailsUrl}>
                            View Details <ChevronRight />
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* --------------------  Section 5: What We Do (HTML-style bg cards)  -------------------- */}
      {whatWeDoSection && (
        <div className="what-we-do">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="text-center mb-5 main-head">
                  {whatWeDoData.heading || "What We Do"}
                </h2>
              </div>

              {servicesList.map((service, idx) => {
                // First card is wider (col-lg-8), rest are col-lg-4 — matching HTML exactly.
                const colClass =
                  idx === 0 ? "col-lg-8 col-md-6" : "col-lg-4 col-md-6";
                const bgImage =
                  service.image_url || service.background_image || "";

                return (
                  <div className={colClass} key={idx}>
                    <div
                      className="what-bg"
                      style={
                        bgImage
                          ? {
                              backgroundImage: `url(${normalizeImageUrl(bgImage)})`,
                            }
                          : undefined
                      }
                    >
                      <div className="what-container">
                        <h4>{service.title}</h4>
                        {service.description && <p>{service.description}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* --------------------  Section 6: Mission & Vision  -------------------- */}
      {missionVisionSection && (
        <div className="mission-vision">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <div
                  className="company-goal"
                  style={{
                    backgroundImage: visionData.background_image
                      ? `url(${normalizeImageUrl(visionData.background_image)})`
                      : "none",
                  }}
                >
                  <h4>{visionData.title || "Our Vision"}</h4>
                  {visionData.description && <p>{visionData.description}</p>}
                </div>
              </div>
              <div className="col-md-6">
                <div
                  className="company-goal"
                  style={{
                    backgroundImage: missionData.background_image
                      ? `url(${normalizeImageUrl(
                          missionData.background_image,
                        )})`
                      : "none",
                  }}
                >
                  <h4>{missionData.title || "Our Mission"}</h4>
                  {missionData.description && <p>{missionData.description}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;
